import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Grid, useTheme, useMediaQuery } from "@mui/material";
import { Trans } from "@lingui/react/macro";
import { checkEconomicIntegration } from "../../store/economic.tsx";
import { checkIntempusIntegration } from "../../store/intempus.tsx";
import { checkFenerumIntegration } from "../../store/fenerum.tsx";
import type { RootState, AppDispatch } from "../../store/types.tsx";
import { INTEGRATIONS } from "./integrationMetadata.ts";
import IntegrationCard from "./IntegrationCard.tsx";
import IntegrationDetailModal from "./IntegrationDetailModal.tsx";
import type { IntegrationMeta } from "./integrationMetadata.ts";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";

interface IntegrationsPageProps {
  onBack: () => void;
  openDrawer?: boolean;
}

const IntegrationsPage: React.FC<IntegrationsPageProps> = ({
  onBack,
  openDrawer = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isScreenBiggerThanOrEqualToMd = useMediaQuery(
    theme.breakpoints.up("md"),
  );

  const { hasIntegration: hasEconomic, isLoading: isLoadingEconomic } =
    useSelector((state: RootState) => state.economic);
  const { hasIntegration: hasIntempus, isLoading: isLoadingIntempus } =
    useSelector((state: RootState) => state.intempus);
  const { hasIntegration: hasFenerum, isLoading: isLoadingFenerum } =
    useSelector((state: RootState) => state.fenerum);

  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationMeta | null>(null);

  // Fetch connection status for all integrations on mount
  useEffect(() => {
    dispatch(checkEconomicIntegration());
    dispatch(checkIntempusIntegration());
    dispatch(checkFenerumIntegration());
  }, [dispatch]);

  const getConnectionStatus = (
    identifier: string,
  ): { isConnected: boolean; isLoading: boolean } => {
    switch (identifier) {
      case "economic":
        return { isConnected: hasEconomic, isLoading: isLoadingEconomic };
      case "intempus":
        return { isConnected: hasIntempus, isLoading: isLoadingIntempus };
      case "fenerum":
        return { isConnected: hasFenerum, isLoading: isLoadingFenerum };
      case "google_sheets":
        // Always show as "Available" — no API call due to side effects
        return { isConnected: false, isLoading: false };
      default:
        return { isConnected: false, isLoading: false };
    }
  };

  return (
    <Box
      sx={[
        {
          position: "fixed",
          top: "50%",
          left: "calc(50%)",
          transform: "translate(-50%, -50%)",
          height: "100%",
          width: "min(100vw, 100vh)",
          "@media (min-width: 1200px) and (-webkit-min-device-pixel-ratio: 2), (min-width: 1200px) and (min-resolution: 192dpi)":
            {
              width: "900px",
            },
          backgroundColor: theme.palette.secondary.main,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: theme.transitions.create(["left", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        },
        isScreenBiggerThanOrEqualToMd &&
          openDrawer && {
            left: `calc(50% + ${SETTINGS.drawerWidth / 2}px)`,
            transition: theme.transitions.create(["left", "width"], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
      ]}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
          py: { xs: 3, sm: 4 },
          scrollbarColor: "var(--tally-brown) var(--tally-white)",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            color="primary.main"
            sx={{
              cursor: "pointer",
              mb: 1,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={onBack}
          >
            <Trans context="Back link from integrations page to conversations">
              ← Back to conversations
            </Trans>
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            <Trans context="Integrations page title">Integrations</Trans>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <Trans context="Integrations page subtitle">
              Connect your apps and services so Tally can access your data.
            </Trans>
          </Typography>
        </Box>

        {/* Card grid */}
        <Grid container spacing={2}>
          {INTEGRATIONS.map((integration) => {
            const { isConnected, isLoading } = getConnectionStatus(
              integration.identifier,
            );
            return (
              <Grid
                key={integration.identifier}
                size={{ xs: 12, sm: 6, md: 6 }}
              >
                <IntegrationCard
                  integration={integration}
                  isConnected={isConnected}
                  isLoading={isLoading}
                  onClick={() => setSelectedIntegration(integration)}
                />
              </Grid>
            );
          })}
        </Grid>

        {/* Detail modal */}
        {selectedIntegration && (
          <IntegrationDetailModal
            open={!!selectedIntegration}
            onClose={() => setSelectedIntegration(null)}
            integration={selectedIntegration}
            isConnected={
              getConnectionStatus(selectedIntegration.identifier).isConnected
            }
          />
        )}
      </Box>
    </Box>
  );
};

export default IntegrationsPage;
